import { HttpContextToken } from '@angular/common/http';
import { EntityKey } from '../constants/entity-registry';

/**
 * HTTP CONTEXT TOKENS
 *
 * These tokens allow you to customize toast behavior on a per-request basis.
 * Use them with HttpClient's context option.
 *
 * Example:
 * ```typescript
 * import { HttpContext } from '@angular/common/http';
 * import { SUPPRESS_SUCCESS_TOAST, CUSTOM_SUCCESS_MESSAGE } from '@o2/toast-message';
 *
 * // Suppress automatic success toast
 * this.http.post(url, data, {
 *   context: new HttpContext().set(SUPPRESS_SUCCESS_TOAST, true)
 * });
 *
 * // Use custom success message
 * this.http.post(url, data, {
 *   context: new HttpContext().set(
 *     CUSTOM_SUCCESS_MESSAGE,
 *     'Listino creato e aggiunto alla categoria Premium'
 *   )
 * });
 * ```
 */

/**
 * Suppress automatic success toast for this request.
 * Useful for operations that need custom handling or silent operations.
 *
 * @default false
 */
export const SUPPRESS_SUCCESS_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Suppress automatic error toast for this request.
 * Useful when you want to handle errors manually in the component.
 *
 * @default false
 */
export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Provide a custom success message instead of the auto-generated one.
 * Takes precedence over automatic message generation.
 *
 * Use this for:
 * - Complex operations that need contextual information
 * - Operations that affect multiple entities
 * - Operations with business-specific messaging
 *
 * Example:
 * ```typescript
 * CUSTOM_SUCCESS_MESSAGE, 'Listino "Premium" creato e 5 voci aggiunte'
 * ```
 *
 * @default null
 */
export const CUSTOM_SUCCESS_MESSAGE = new HttpContextToken<string | null>(() => null);

/**
 * Provide a custom error message instead of the auto-generated one.
 * Takes precedence over automatic message generation.
 *
 * Use this for:
 * - Business-specific error conditions
 * - Errors that need contextual explanation
 * - User-friendly error messages for specific endpoints
 *
 * Example:
 * ```typescript
 * CUSTOM_ERROR_MESSAGE, 'Impossibile eliminare il listino: è utilizzato in 3 contratti attivi'
 * ```
 *
 * @default null
 */
export const CUSTOM_ERROR_MESSAGE = new HttpContextToken<string | null>(() => null);

/**
 * Override automatic entity detection with a specific entity key.
 * Useful for complex endpoints that don't follow standard REST patterns.
 *
 * Example:
 * ```typescript
 * // POST to /api/complex-operation but want price-list messages
 * ENTITY_KEY, 'price-lists'
 * ```
 *
 * @default null (auto-detect from URL)
 */
export const ENTITY_KEY = new HttpContextToken<EntityKey | null>(() => null);

/**
 * Provide a description to show below the success message.
 * When set, displays a toast with title (the success message) and description.
 *
 * Use this for:
 * - Operations that trigger async side effects (e.g., contract creation)
 * - Status changes that have consequences the user should know about
 *
 * Example:
 * ```typescript
 * // Quote accepted - inform about contract creation
 * context: new HttpContext()
 *   .set(CUSTOM_SUCCESS_DESCRIPTION, 'Il contratto sarà disponibile a breve nella sezione Contratti')
 * ```
 *
 * @default null
 */
export const CUSTOM_SUCCESS_DESCRIPTION = new HttpContextToken<string | null>(
  () => null,
);
